// EnhancedTextNode.js

import { useState, useEffect, useRef } from 'react';
import { Position, useUpdateNodeInternals } from 'reactflow';
import { BaseNode } from './BaseNode';

export const EnhancedTextNode = ({ id, data }) => {
  const [text, setText] = useState(data?.text || '{{input}}');
  const [variables, setVariables] = useState([]);
  const textareaRef = useRef(null);
  const updateNodeInternals = useUpdateNodeInternals();

  // Extract variables from text using regex
  const extractVariables = (text) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      const varName = match[1].trim();
      if (varName && !matches.includes(varName)) {
        matches.push(varName);
      }
    }
    return matches;
  };

  // Update variables when text changes
  useEffect(() => {
    const newVariables = extractVariables(text);
    setVariables(newVariables);
  }, [text]);

  // Notify React Flow when handles change
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, variables, updateNodeInternals]);

  // Auto-resize textarea logic can be kept here or inline
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [text]);

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  // Dynamic handles based on variables
  const variableHandles = variables.map((variable, index) => ({
    id: `${id}-${variable}`,
    type: 'target',
    position: Position.Left,
    style: {
      top: `${index * 20 + 50}px`, // Staggered position
      left: '-4px'
    }
  }));


  const allHandles = [
    ...variableHandles,
    {
      id: `${id}-output`,
      type: 'source',
      position: Position.Right,
      style: { right: '-4px' }
    }
  ];
  console.log('allHandles: ', allHandles);
  return (
    <BaseNode
      id={id}
      data={data}
      type="text"
      title="Text"
      handles={allHandles}
      style={{
        // Dynamic width based on text, restricted to reasonable bounds
        width: Math.min(400, Math.max(200, text.length * 8 + 40)),
        height: 'auto'
      }}
    >
      <div className="flex flex-col gap-2 p-3">
        <label className="text-xs text-pipeline-secondary font-medium">
          Text
          <span className="ml-2 opacity-50 text-[10px]">(use {'{{var}}'} for variables)</span>
        </label>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          placeholder="Enter text with {{variables}}"
          className="
            w-full min-h-[60px] max-h-[200px] 
            px-3 py-2 text-sm 
            bg-pipeline-bg border border-pipeline-border 
            rounded-lg text-white 
            focus:outline-none focus:ring-2 focus:ring-pipeline-accent 
            placeholder-gray-500 
            resize-none overflow-hidden font-mono
          "
          style={{ height: 'auto' }}
        />

        {variables.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {variables.map(v => (
              <span key={v} className="px-1.5 py-0.5 text-[10px] rounded-sm bg-pipeline-accent/20 text-pipeline-accent border border-pipeline-accent/30">
                {v}
              </span>
            ))}
          </div>
        )}
      </div>
    </BaseNode>
  );
};
